import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { User, LogOut, Pencil, X, Shield, Flag, CircleDot, Car, Trophy, MessageSquare, Settings, Bell } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const TEAMS = [
  { name: "McLaren", colour: "FF8000" },
  { name: "Ferrari", colour: "E8002D" },
  { name: "Red Bull Racing", colour: "3671C6" },
  { name: "Mercedes", colour: "27F4D2" },
  { name: "Aston Martin", colour: "229971" },
  { name: "Alpine", colour: "FF87BC" },
  { name: "Williams", colour: "64C4FF" },
  { name: "Racing Bulls", colour: "6692FF" },
  { name: "Haas", colour: "B6BABD" },
  { name: "Audi", colour: "B82100" },
  { name: "Cadillac", colour: "C0C0C0" },
] as const;

const AVATARS = [
  { id: "helmet", label: "Helmet", Icon: Shield },
  { id: "flag", label: "Flag", Icon: Flag },
  { id: "wheel", label: "Wheel", Icon: CircleDot },
  { id: "car", label: "Car", Icon: Car },
  { id: "trophy", label: "Trophy", Icon: Trophy },
] as const;

function AvatarDisplay({ avatarId, teamColour, size = "lg" }: { avatarId: string | null; teamColour: string | null; size?: "lg" | "sm" }) {
  const avatar = AVATARS.find((a) => a.id === avatarId);
  const Icon = avatar?.Icon ?? User;
  const color = teamColour ? `#${teamColour}` : "hsl(var(--primary))";
  const dim = size === "lg" ? "h-20 w-20" : "h-14 w-14";
  const iconSize = size === "lg" ? 36 : 28;

  return (
    <div
      className={cn("flex items-center justify-center rounded-full border-4", dim)}
      style={{ borderColor: color, backgroundColor: `${color}20` }}
    >
      <Icon size={iconSize} style={{ color }} />
    </div>
  );
}

const Profile = () => {
  const { user, profile, loading, signOut, refetchProfile } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState("");
  const [feedbackSaving, setFeedbackSaving] = useState(false);

  // Edit state
  const [displayName, setDisplayName] = useState("");
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(null);

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.display_name ?? "");
      setSelectedTeam(profile.favourite_team ?? null);
      setSelectedAvatar(profile.avatar_icon ?? null);
    }
  }, [profile]);

  if (loading) return null;

  if (!user) {
    return (
      <div className="flex min-h-[80vh] flex-col items-center justify-center px-6">
        <User className="mb-4 h-12 w-12 text-muted-foreground" />
        <h1 className="mb-2 text-xl font-bold">Profile</h1>
        <p className="mb-6 text-muted-foreground">Sign in to view your profile.</p>
        <Button className="font-semibold" asChild>
          <Link to="/sign-in">Sign In</Link>
        </Button>
      </div>
    );
  }

  const editTeamColour = TEAMS.find((t) => t.name === selectedTeam)?.colour ?? null;

  const startEditing = () => {
    setDisplayName(profile?.display_name ?? "");
    setSelectedTeam(profile?.favourite_team ?? null);
    setSelectedAvatar(profile?.avatar_icon ?? null);
    setEditing(true);
  };

  const cancelEditing = () => setEditing(false);

  const handleSave = async () => {
    if (!displayName.trim()) {
      toast({ title: "Display name is required", variant: "destructive" });
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        display_name: displayName.trim(),
        favourite_team: selectedTeam,
        team_colour: editTeamColour,
        avatar_icon: selectedAvatar,
      })
      .eq("id", user.id);
    setSaving(false);
    if (error) {
      toast({ title: "Failed to save", description: error.message, variant: "destructive" });
    } else {
      await refetchProfile();
      setEditing(false);
      toast({ title: "Profile updated!" });
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const handleFeedbackSubmit = async () => {
    if (!feedbackMsg.trim() || !user) return;
    setFeedbackSaving(true);
    const { error } = await supabase.from("feedback").insert({ user_id: user.id, message: feedbackMsg.trim() });
    setFeedbackSaving(false);
    if (error) {
      toast({ title: "Failed to send feedback", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Thanks for your feedback!" });
      setFeedbackMsg("");
      setFeedbackOpen(false);
    }
  };

  const memberSince = profile?.created_at ? format(new Date(profile.created_at), "MMMM yyyy") : "—";

  return (
    <div className="px-4 py-6 md:px-8 md:py-10">
      <div className="mx-auto max-w-lg space-y-6">
        {/* Header with avatar */}
        <div className="flex flex-col items-center gap-3 text-center">
          <AvatarDisplay
            avatarId={editing ? selectedAvatar : (profile?.avatar_icon ?? null)}
            teamColour={editing ? editTeamColour : (profile?.team_colour ?? null)}
          />
          {!editing && (
            <>
              <h1 className="text-2xl font-bold">{profile?.display_name || "—"}</h1>
              {profile?.favourite_team && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold"
                  style={{
                    backgroundColor: `#${profile.team_colour ?? "888"}20`,
                    color: `#${profile.team_colour ?? "888"}`,
                    border: `1px solid #${profile.team_colour ?? "888"}40`,
                  }}
                >
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: `#${profile.team_colour ?? "888"}` }} />
                  {profile.favourite_team}
                </span>
              )}
            </>
          )}
        </div>

        {/* View Mode */}
        {!editing && (
          <Card>
            <CardContent className="space-y-4 p-6">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">Email</span>
                <span className="text-sm">{user.email}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">Member since</span>
                <span className="text-sm">{memberSince}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">Email notifications</span>
                </div>
                <Switch
                  checked={profile?.email_notifications_enabled ?? true}
                  onCheckedChange={async (checked) => {
                    const { error } = await supabase
                      .from("profiles")
                      .update({ email_notifications_enabled: checked })
                      .eq("id", user.id);
                    if (error) {
                      toast({ title: "Failed to update", description: error.message, variant: "destructive" });
                    } else {
                      await refetchProfile();
                      toast({ title: checked ? "Notifications enabled" : "Notifications disabled" });
                    }
                  }}
                />
              </div>
              <Button variant="outline" className="w-full font-semibold" onClick={startEditing}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit Profile
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Edit Mode */}
        {editing && (
          <Card>
            <CardContent className="space-y-5 p-6">
              {/* Display Name */}
              <div className="space-y-2">
                <Label htmlFor="editName">Display Name</Label>
                <Input
                  id="editName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value.slice(0, 20))}
                  maxLength={20}
                />
                <p className="text-xs text-muted-foreground">{displayName.length}/20 characters</p>
              </div>

              {/* Team Picker */}
              <div className="space-y-2">
                <Label>Favourite Team</Label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {TEAMS.map((team) => {
                    const isSelected = selectedTeam === team.name;
                    return (
                      <button
                        key={team.name}
                        type="button"
                        onClick={() => setSelectedTeam(team.name)}
                        className={cn(
                          "flex flex-col items-center gap-1 rounded-lg border-2 p-2 text-xs font-semibold transition-all",
                          isSelected ? "scale-[1.03] shadow-md" : "border-border hover:border-muted-foreground/40"
                        )}
                        style={isSelected ? { borderColor: `#${team.colour}`, backgroundColor: `#${team.colour}18` } : undefined}
                      >
                        <span className="h-4 w-4 rounded-full" style={{ backgroundColor: `#${team.colour}` }} />
                        {team.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Avatar Picker */}
              <div className="space-y-2">
                <Label>Avatar</Label>
                <div className="flex flex-wrap justify-center gap-3">
                  {AVATARS.map(({ id, label, Icon }) => {
                    const isSelected = selectedAvatar === id;
                    const ringColor = editTeamColour ? `#${editTeamColour}` : "hsl(var(--primary))";
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setSelectedAvatar(id)}
                        className={cn(
                          "flex flex-col items-center gap-1 rounded-xl border-2 p-3 transition-all",
                          isSelected ? "scale-[1.08] shadow-lg" : "border-border hover:border-muted-foreground/40"
                        )}
                        style={isSelected ? { borderColor: ringColor, backgroundColor: `${ringColor}18` } : undefined}
                      >
                        <div
                          className="flex h-10 w-10 items-center justify-center rounded-full"
                          style={isSelected ? { backgroundColor: `${ringColor}25` } : { backgroundColor: "hsl(var(--muted))" }}
                        >
                          <Icon size={20} style={isSelected ? { color: ringColor } : undefined} />
                        </div>
                        <span className="text-[10px] font-medium">{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Save / Cancel */}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={cancelEditing} disabled={saving}>
                  <X className="mr-1 h-4 w-4" />
                  Cancel
                </Button>
                <Button className="flex-1 font-semibold" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving…" : "Save Changes"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Admin */}
        {profile?.is_admin && (
          <Button variant="outline" className="w-full font-semibold" asChild>
            <Link to="/admin">
              <Settings className="mr-2 h-4 w-4" />
              Admin
            </Link>
          </Button>
        )}

        {/* Feedback */}
        <Button variant="outline" className="w-full font-semibold" onClick={() => setFeedbackOpen(true)}>
          <MessageSquare className="mr-2 h-4 w-4" />
          Share Feedback
        </Button>

        <Dialog open={feedbackOpen} onOpenChange={setFeedbackOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Got feedback or a feature idea?</DialogTitle>
              <DialogDescription>We'd love to hear from you.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Textarea
                placeholder="Tell us what you think..."
                value={feedbackMsg}
                onChange={(e) => setFeedbackMsg(e.target.value.slice(0, 500))}
                maxLength={500}
                rows={4}
              />
              <p className="text-xs text-muted-foreground text-right">{feedbackMsg.length}/500</p>
            </div>
            <Button
              className="w-full font-semibold bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={!feedbackMsg.trim() || feedbackSaving}
              onClick={handleFeedbackSubmit}
            >
              {feedbackSaving ? "Sending…" : "Submit"}
            </Button>
          </DialogContent>
        </Dialog>

        {/* Sign Out */}
        <Button variant="destructive" className="w-full font-semibold" onClick={handleSignOut}>
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </Button>
      </div>
    </div>
  );
};

export default Profile;
